// Library methods
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { HubConnection } from "@microsoft/signalr";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import DryEyeOptions from "./DryEyeOptions/DryEyeOptions";
import TitleWithTooltip from "src/components/shared/text/TitleWithTooltip/TitleWithTooltip";
import SelectDropdown from "src/components/shared/inputs/SelectDropdown/SelectDropdown";
import { LensParameterUpdate } from "../../LensSimulation/LensSimulation";

// Contexts
import { useLiveUpdateContext } from "src/contexts/live-update/LiveUpdateContext";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";
import { useSimulationEnvironmentTypes } from "src/services/clinics/hooks/clinic-lenses/simulation-environments/useSimulationEnvironmentTypes";

// Services
import { deviceUpdateSimParameter } from "src/services/signalR/api";

// Types
import { ExamLensType } from "src/services/exams/types";

// Constants
import { ENABLED_SIM_ENVIRONMENTS } from "src/constants/enums";

type DryEyeSimulationOptionsType = {
  connection: HubConnection | null;
  currentProfile: ExamLensType | undefined;
  currentSimEnv: string;
  isCurrentProfileActionDisabled: boolean;
  sendProfileUpdate: (
    profileName: string,
    update: LensParameterUpdate
  ) => Promise<void>;
};

const DryEyeSimulationOptions = ({
  connection,
  currentProfile,
  currentSimEnv,
  isCurrentProfileActionDisabled,
  sendProfileUpdate,
}: DryEyeSimulationOptionsType) => {
  const { t } = useTranslation();
  const { device } = useLiveUpdateContext();
  const { getToken } = useTokenAccess();
  const currentProfileName = currentProfile?.name;

  const [envType, setEnvType] = useState({
    displayName: t(currentSimEnv),
    value: currentSimEnv,
  });

  const {
    data: envData,
    isLoading: isLoadingEnvs,
    isError: isErrorEnvs,
  } = useSimulationEnvironmentTypes();

  const formattedEnvOptions = useMemo(
    () =>
      (envData ?? [])
        ?.filter((data) => ENABLED_SIM_ENVIRONMENTS.includes(data))
        ?.map((data) => ({
          displayName: t(data),
          value: data,
        })),
    [t, envData]
  );

  const hasSimEnvs = useMemo(
    () =>
      formattedEnvOptions &&
      formattedEnvOptions?.length > 0 &&
      !isLoadingEnvs &&
      !isErrorEnvs,
    [formattedEnvOptions, isLoadingEnvs, isErrorEnvs]
  );

  // pass parameters to update on the current profile
  const sendProfileParameterUpdate = useCallback(
    async (update: LensParameterUpdate) => {
      if (!currentProfileName) return;
      await sendProfileUpdate(currentProfileName, update);
    },
    [currentProfileName, sendProfileUpdate]
  );

  const sendSimParameterUpdate = useCallback(
    async (simulationEnvironmentType: string) => {
      if (!device?.id || !connection) return;

      try {
        const token = await getToken();
        await deviceUpdateSimParameter(
          token,
          device.id,
          { simulationEnvironmentType },
          connection
        );
      } catch (e) {
        console.error(e);
      }
    },
    [device?.id, connection, getToken]
  );

  return (
    <Box display="flex" flexDirection="column" gap="8px">
      <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
        {t("Dry Eye Options")}
      </Typography>

      {currentProfile ? (
        <DryEyeOptions
          key={currentProfileName}
          currentLens={currentProfile}
          isCurrentLensActionDisabled={isCurrentProfileActionDisabled}
          sendLensParameterUpdate={sendProfileParameterUpdate}
        />
      ) : (
        <Typography sx={{ fontSize: "14px" }}>
          {t("Select a profile to change its parameters.")}
        </Typography>
      )}

      {hasSimEnvs && (
        <>
          <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
            {t("Simulation Options")}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(1, 1fr)",
                sm: "repeat(2, 1fr)",
              },
              gap: "8px",
            }}
          >
            <Box display="flex" flexDirection="column" gap="4px">
              <TitleWithTooltip
                title={t("Simulation Environment")}
                tooltip=""
                titleStyle={{ fontSize: "12px" }}
                tooltipStyle={{ width: "16px", height: "16px" }}
              />
              <SelectDropdown
                id="dry-eye-sim-env-type-dropdown"
                dropdownOptions={formattedEnvOptions}
                value={envType.displayName}
                handleChange={async (event) => {
                  const value = formattedEnvOptions?.find(
                    (option) => option.displayName === event.target.value
                  );

                  if (value) {
                    setEnvType({ ...value });
                    await sendSimParameterUpdate(value.value);
                  }
                }}
              />
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default DryEyeSimulationOptions;
